<template>
  <div class="c-accordion-fields">
    <c-collapsible-card
      ref="collapsibleCard"
      class="accordion-card"
      :opened="isOpen"
      no-border
      transparent
      clickable
      block-header-hover
      @click="isOpen = !isOpen"
    >
      <template #header>
        <c-typo
          class="accordion-label"
          type="h2"
          weight="medium"
          opacity="base-80"
        >
          {{ label }}
        </c-typo>

        <c-counter
          v-if="!isOpen"
          class="accordion-counter"
          :value="count"
          :has-empty-value="count === 0"
          white
        />
      </template>

      <template #body>
        <div ref="accordionBody" class="accordion-body">
          <div class="accordion-fields">
            <div v-for="field in fieldEntries" :key="field.key" class="accordion-field">
              <c-form-builder-field
                :field-name="field.key"
                :field-schema="fieldSchema(field)"
                :form-schema="fields"
                :form-value="innerValue"
                :value="fieldValue(field)"
                :field-options="field.options || []"
                :has-validation="false"
                @input="val => handleInput(field, val)"
                @add="item => handleAdd(field, item)"
                @remove="item => handleRemove(field, item)"
              />
            </div>
          </div>
        </div>
      </template>
    </c-collapsible-card>

    <c-divider class="bottom-divider" />
  </div>
</template>

<script setup>
import { ref, computed, provide, onMounted, onBeforeUnmount } from 'vue'
import CCollapsibleCard from '@convenia/components/CCollapsibleCard'
import CCounter from '@convenia/components/CCounter'
import CDivider from '@convenia/components/CDivider'
import CTypo from '@convenia/components/CTypo'
import CFormBuilderField from '@convenia/components/CFormBuilder/fragments/FormField.vue'

const props = defineProps({
  label: {
    type: String,
    required: true
  },

  fields: {
    type: Object,
    default: () => ({})
  },

  value: {
    type: Object,
    default: null
  },

  modelValue: {
    type: Object,
    default: () => ({})
  },

  initialValue: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits([ 'input', 'update:modelValue' ])

const ARRAY_TYPES = [ 'multi-select', 'multi-check' ]
const [ MULTI_SELECT, MULTI_CHECK ] = ARRAY_TYPES

provide('_enableSubmitFormat', () => {})

const isOpen = ref(false)
const collapsibleCard = ref(null)
const accordionBody = ref(null)
let resizeObserver = null

const getFieldCount = (type, val) => {
  if (ARRAY_TYPES.includes(type)) return val?.length ?? 0
  if (type === 'check') return val === true ? 1 : 0
  return val != null ? 1 : 0
}

const innerValue = computed(() => props.value ?? props.modelValue)

const fieldEntries = computed(() =>
  Object.entries(props.fields ?? {}).map(([ key, field ]) => ({ key, ...field }))
)

const count = computed(() =>
  fieldEntries.value.reduce((sum, { key, type }) => {
    const val = innerValue.value?.[key]
    if (val === props.initialValue?.[key]) return sum
    return sum + getFieldCount(type, val)
  }, 0)
)

defineExpose({ count })

onMounted(() => {
  if (!accordionBody.value) return
  resizeObserver = new ResizeObserver(() => collapsibleCard.value?.resize())
  resizeObserver.observe(accordionBody.value)
})

onBeforeUnmount(() => resizeObserver?.disconnect())

const emitChange = (key, value) => {
  const newValue = { ...innerValue.value, [key]: value }
  emit('input', newValue)
  emit('update:modelValue', newValue)
}

const isAllSelect = (field) =>
  field.type === MULTI_SELECT && field.allOption && !innerValue.value[field.key]?.length

const fieldValue = (field) =>
  isAllSelect(field) ? field.options ?? [] : innerValue.value[field.key]

const fieldSchema = ({ key, ...rest }) => ({
  validation: {},
  ...rest,
  ...(isAllSelect({ key, ...rest }) && { autoSelectAll: true })
})

const handleInput = (field, val) => {
  if (field.type === MULTI_CHECK && Array.isArray(val)) return emitChange(field.key, val)
  if (field.type === MULTI_SELECT) return

  emitChange(field.key, val)
}

const handleAdd = (field, item) => {
  if (field.type !== MULTI_SELECT) return
  emitChange(field.key, [ ...(innerValue.value[field.key] ?? []), item ])
}

const handleRemove = (field, item) => {
  if (field.type !== MULTI_SELECT) return
  const current = innerValue.value[field.key] ?? []

  if (typeof item === 'number') {
    return emitChange(field.key, current.filter((_, i) => i !== item))
  }

  if (field.trackBy) {
    return emitChange(field.key, current.filter(v => v?.[field.trackBy] !== item[field.trackBy]))
  }

  emitChange(field.key, current.filter(v => v !== item))
}
</script>

<script>
export default { name: 'CAccordionFields' }
</script>

<style lang="scss">
.c-accordion-fields {
  width: 100%;

  & .accordion-card {
    & .header-wrapper {
      padding: 10px 0;

      & > .content {
        align-items: center;
        gap: 10px;
      }
    }

    &.--opened .body-wrapper {
      overflow: visible;
    }
  }

  & .accordion-label {
    flex: 1;
  }

  & .accordion-body {
    display: flex;
    flex-direction: column;
    padding-top: 20px;
  }

  & .accordion-fields {
    padding: 0 0 16px 0;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  & .accordion-field .c-form-field {
    margin: 0;
    max-width: 100%;
    width: 100%;
    grid-template-columns: 1fr;
  }

  & .bottom-divider {
    margin-top: 4px;
  }
}
</style>
